import React, { useMemo } from 'react';
import { useLocal, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import CardBox from '@/pages/dashboard/components/CardBox';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { DATA_TYPE_OBJECT, getName, selectValByCondition } from '@home/utils/common';
import { IBaseTooltipProps } from '@/pages/dashboard/components/EchartsTooltip/type';
import classNames from 'classnames';
import {
  addChartLabel,
  xAxisHighLight,
  xAxisLabelFormat,
} from '@/pages/dashboard/components/Chart/_config/plug';
import { handleAxisFormat, jumpDetail, openWithParams } from '@/pages/dashboard/utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';
import useStudioExplain from '@home/hooks/useStudioExplain';
import SvgIcon from '@home/components/SvgIcon';
import { mapObjectValue } from '@home/pages/studio/utils';
import { extractParamsFromCarryFilter, getFilterConfig } from '../utils';
import { JumpType, navigate } from '@home/utils/utils';

export interface IRollingRetentionCardProps extends BaseComponentProps {
  tooltipConfig: IBaseTooltipProps['options'];
  dataformatRule: string;
  titleEn: string;
  titleCn: string;
  explainEn?: string;
  explainCn?: string;
  componentMetrics: {
    metricKey: string;
    nameEn: string;
    nameCn: string;
    tooltipNameEn?: string;
    tooltipNameCn?: string;
    tooltipNameWidthEn?: number;
    tooltipNameWidthCn?: number;
    lineType: string;
  }[];
  [key: string]: any;
}

const RollingRetentionCard = (props: IRollingRetentionCardProps) => {
  const {
    style,
    className,
    dataKey = 'data',
    data,
    iconUrl,
    titleEn,
    titleCn,
    explainEn,
    explainCn,
    rainbowKey,
    componentMetrics = [],
    dataformatRule,
    filter,
    link,
    jumpType = JumpType.Window,
    jumpParams,
    height = 254,
    extraCarryParams={},
    cardType,
    filterConfig
  } = props || {};

  const { errorMsg, loading, data: chartData = {} } = data[dataKey] || {};

  const { isEN } = useLocal();

  const { getWeekDayStr } = useWeekDay();
  const { tipEn, tipCn } = useStudioExplain(rainbowKey, explainEn, explainCn);
  const { date_type: dataType, isSingle,start_time: startTime, end_time: endTime, payType, selectorList} = filter || {};
  const carryFilter = getFilterConfig(selectorList, filterConfig);

  const { xData, seriesData } = useMemo(() => {
    const seriesData = componentMetrics?.map((item) => {
      const { metricKey, nameCn, nameEn, lineType } = item;
      const name = getName(isEN, nameEn, nameCn);

      return {
        type: 'line',
        name,
        lineStyle: {
          type: lineType,
        },
        data: chartData?.[metricKey]?.map((item: any) => item.value) || [],
      };
    });
    return {
      xData:
        chartData?.[componentMetrics[0]?.metricKey]?.map(
          (item: any) => item.date,
        ) || [],
      seriesData,
    };
  }, [componentMetrics, isEN, chartData]);

  // 浮窗
  const getTooltipText = (params: any) =>
    getEchartTooltipString({
      table: params?.map((item: any, index: number) => {
        const { componentIndex, seriesName, color, dataIndex } = item;
        const {
          metricKey,
          tooltipNameEn,
          tooltipNameCn,
          tooltipNameWidthEn,
          tooltipNameWidthCn,
        } = componentMetrics[componentIndex] || {};
        // 获取当前hover的原始数据
        const currentOrgData = chartData?.[metricKey]?.[dataIndex] || {};
        const { date, value } = currentOrgData;
        // 有自定义的浮窗名称则优先取
        const name =
          tooltipNameEn && tooltipNameCn
            ? getName(isEN, tooltipNameEn, tooltipNameCn)
            : seriesName;

        const nameWidth = selectValByCondition(
          isEN,
          tooltipNameWidthEn,
          tooltipNameWidthCn,
        );
        // 转换日期格式
        const newDate = dateTimeFormat(date, 'day');
        return {
          style: { marginTop: index === 0 ? undefined : 10, width: '100%' },
          data: [{ name, value, color }],
          columns: [
            {
              key: 'name',
              header: getWeekDayStr(newDate, 'Day'),
              showIcon: true,
              width: nameWidth,
            },
            {
              key: 'value',
              header: '',
              dataFormatRule: dataformatRule,
              align: 'right',
            },
          ],
        };
      }),
    });

  const renderList = useMemo(
    () => [
      {
        type: 'lineChart',
        component: (
          <LineChart
            key={isEN?.toString()}
            hasDataZoom={true}
            height={254}
            xAxisData={xData}
            series={seriesData}
            formatterFunc={getTooltipText}
            notMerge={false}
            setOtherConfigFunc={(opt: any) => {
              // X轴格式化
              opt = xAxisLabelFormat(opt);
              // Y轴格式化
              opt.yAxis.axisLabel.formatter = (value: any) =>
                dataFormat({ value, formatRule: dataformatRule });
              // 折线文字格式化
              opt = addChartLabel(opt, {
                show: true,
                formatter: (params: any) =>
                  dataFormat({
                    value: params.value,
                    formatRule: dataformatRule,
                  }),
                formatNum: (value: string) =>
                  dataFormat({ value, formatRule: dataformatRule }),
              });
              // X轴高亮
              opt = xAxisHighLight(opt, {
                formatter: (params: any) =>
                  handleAxisFormat(params.value, 'Day'),
              });
              return opt;
            }}
          />
        ),
      },
    ],
    [isEN, seriesData, xData],
  );

  const isNodeData = componentMetrics?.every(item => chartData?.[item?.metricKey]?.length === 0);

  // 筛选器继承参数
  const filterCarry = useMemo(() => {
    let baseParams;
  
    if (cardType === 'opinion') {
      baseParams = {
        start_time: startTime,
        end_time: endTime,
        date_type: dataType,
      };
    } else {
      baseParams = {
        start: startTime,
        end: endTime,
        timeType: DATA_TYPE_OBJECT[dataType],
        ...(payType ? { payment_type: payType } : {}),
      };
    }
  
    // 合并额外参数
    return { ...baseParams, ...extractParamsFromCarryFilter(extraCarryParams,carryFilter) };
  }, [filter, cardType, extraCarryParams, carryFilter]);
  
    
  // 卡片下钻
  const onClick = () => {
    if (link) {
      // 筛选器继承跳转
      if (jumpParams?.filterValue && isSingle && jumpParams?.isComplete && filter?.jumpTime) {
        jumpParams.filterValue.rangeTime = filter?.jumpTime;
      }

      navigate(link, { jumpType, windowParams: filterCarry });

      // 下钻逻辑
      // if (jumpParams?.filterValue) {
      //   jumpParams.filterValue = mapObjectValue(jumpParams.filterValue, filter);
      // }
      // jumpDetail(link, jumpParams);
    }
  };

  return (
    <CardBox
      style={style}
      className={classNames(className, 'dbh-dl2-rolling-retention-container', {'no-link': !link })}
      titleCom={
        <CommonBubble content={getName(isEN, tipEn, tipCn)}>
          <div className="dbh-dl2-rolling-retention-title" onClick={link ? onClick : undefined}>
            {iconUrl && <img src={iconUrl} />}
            {getName(isEN, titleEn, titleCn)}
            {link && <SvgIcon className='dbh-dl2-rolling-retention-title-icon' type='arrow-right' />}
          </div>
        </CommonBubble>
      }
      // subTitleCom={
      //   <div className="avg-data">
      //     {componentMetrics?.map((item) => {
      //       const { avgNameCn, avgNameEn } = item;
      //       const text = getName(isEN, avgNameEn, avgNameCn);
      //       return (
      //         <span key={avgNameEn}>
      //           {dynamicString(text, chartData)}
      //         </span>
      //       );
      //     })}
      //   </div>
      // }
      key={'DL2RollingRetention'}
      height={height}
      renderList={renderList}
      isLoading={loading}
      isError={!!errorMsg}
      isNodata={isNodeData}
    />
  );
};
export default RollingRetentionCard;
